import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { CognitoAuthService } from '@commons/services/cognito-auth/cognito-auth.service';
import { environment } from '@environment';
import {
  ERROR_NO_CURRENT_USER,
  KEY_NOTIFICATION_TOKEN,
  TOKEN_NOTIFICATION,
} from '@commons/constants';
import { StorageService } from '@commons/services/storage/storage.service';
import { FunctionsService } from '@commons/services/functions/functions.service';
import { STORAGE_TYPE } from '@commons/models';

import {
  BID_INVENTORY_PURCHASE_ORDER_SAVE_TOKEN,
  BID_PROCESS_FILE_TOKEN,
} from '../bid/common';

/**
 * /**.
 */
@Injectable()
/**
 * HTTP interceptor that attaches authorization tokens, notification tokens,
 * and LOB headers to outgoing API requests.
 */
export class AppInterceptor implements HttpInterceptor {
  excludeUrls = [
    environment.awsServicesBucket,
    '.s3.amazonaws.com',
    '.s3.us-east-1.amazonaws.com',
    '/assets/i18n/', // Exclude translation files from authentication
  ];
  urlsWithTokenFCM = [
    /**
     * B i d_ i n v e n t o r y_ p u r c h a s e_ o r d e r_ s a v e_ t o k e n,.
     */
    BID_INVENTORY_PURCHASE_ORDER_SAVE_TOKEN,
    BID_PROCESS_FILE_TOKEN,
  ];

  /**
   * Creates an instance of AppInterceptor.
   *
   * @param storageService - Service to access local/session storage.
   * @param functionsService - Service providing shared utility functions.
   */
  constructor(
    private storageService: StorageService,
    private functionsService: FunctionsService,
    private authService: CognitoAuthService,
  ) {}

  /**
   * The intercept function retrieves the current session's id token and adds it to the request
   * before passing it to the next handler.
   * @param {HttpRequest<unknown>} request It represents the HTTP request that is being intercepted and modified
   * before being passed to the next handler in the chain.
   * @param {HttpHandler} next - The `next` parameter in the `intercept` function is an `HttpHandler`
   * type.
   * @returns The `intercept` method returns an Observable of type `HttpEvent<unknown>`.
   */
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Check if URL should be excluded from authentication
    if (this.excludeUrls.some((url) => request.url.includes(url))) {
      return next.handle(request);
    }

    // Try to get current session, but continue without auth if no session exists
    return from(this.authService.currentSession()).pipe(
      switchMap(({ tokens }) => {
        const accessToken = tokens.idToken;
        request = this.addToken(request, accessToken);
        request = this.addLobHeader(request);

        if (this.shouldAddToken(request.url)) {
          request = this.addTokenNotification(request);
        }
        return next.handle(request);
      }),
      catchError((error) => {
        // Silently continue without auth if no session (user not logged in)
        if (error.message === 'No current session' || error == ERROR_NO_CURRENT_USER) {
          return next.handle(request);
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * The `addToken` function adds an Authorization token to the headers of an HTTP request.
   * @param {HttpRequest<unknown>} request - HttpRequest<unknown> is an object representing an HTTP request in a web
   * application.
   * @param {string} token - The `token` parameter
   * @returns {HttpRequest<unknown>} clone `request` add token
   */
  addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
    });
  }

  /**
   * The `shouldAddToken` function checks if the URL should include the token.
   * @param {string} url - The request URL.
   * @returns {boolean} - Whether the token should be added.
   */
  shouldAddToken(url: string): boolean {
    return this.urlsWithTokenFCM.some(
      (endUrl) => url.split('/').pop() == endUrl,
    );
  }

  /**
   * The `addTokenNotification` function adds a notification token to the headers of an HTTP request.
   * @param {HttpRequest<unknown>} request - HttpRequest<unknown> is an object representing an HTTP request in a web
   * @returns {HttpRequest<unknown>} clone `request` add token
   */
  addTokenNotification(request: HttpRequest<unknown>): HttpRequest<unknown> {
    return request.clone({
      headers: request.headers.set(
        KEY_NOTIFICATION_TOKEN,
        this.storageService.getItem<string>(
          TOKEN_NOTIFICATION,
          STORAGE_TYPE.LOCAL,
        ) ?? '',
      ),
    });
  }

  /**
   * Adds the current selected LOB as a header to all outgoing requests.
   * Allows backend services to know the LOB context without parsing the JWT.
   *
   * @param request - The outgoing HTTP request.
   * @returns The cloned request with the LOB header appended.
   */
  addLobHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const allowedPaths = ['/api/afs', '/api/security', '/api/reports'];
    if (!allowedPaths.some((path) => request.url.includes(path))) {
      return request;
    }
    const selectedLob = this.functionsService.getLobSelected();
    if (selectedLob) {
      return request.clone({
        headers: request.headers.set('x-sq-lob', selectedLob),
      });
    }
    return request;
  }
}
